import React, { useState } from "react";
import {
  Button,
  Container,
  Stack,
  IconButton,
  InputAdornment,
  TextField,
  Snackbar,
  SnackbarContent,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { signIn, verifyUser } from "./AuthSignIn";
import { useAppDispatch } from "../../store/hooks";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { setUser } from "../../store/reducers/user.reducer";
import { getUserDetails } from "../../services/APIServices/user.api";
import { setMessage } from "../../store/reducers/message.reducer";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";

const UserLogin = (props: any) => {
  const { redirectRequired = false, redirectTo, onSuccess = () => { } } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otp: "",
  });
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleLogin = async () => {
    if (isLoading) return; // prevent multiple clicks
    setIsLoading(true);

    try {
      if (!formData.email || !formData.password) {
        toast.error("Please enter both email and password.");
        return;
      }

      const response = await signIn(formData);
      const accessToken = response.accessToken;

      if (accessToken) {
        if (!showOTPInput) {
          toast.success("Login successful! 🎉");
        }

        localStorage.setItem("token", accessToken);
        onSuccess?.();

        if (response.otpRequired) {
          setShowOTPInput(true);
        }

        let user = { ...response, userID: response.id };
        getUserDetails(user?.userID)
          .then((res) => {
            if (user?.userID) dispatch(setUser({ ...res, ...user }));
          })
          .catch(() => {
            dispatch(setUser(user));
          })
          .finally(() => {
            if (redirectRequired || location?.state?.redirectTo || redirectTo) {
              navigate(redirectTo ?? location?.state?.redirectTo ?? "/");
            }
          });
      } else if (response.message) {
        setShowOTPInput(true);
        toast.info(response.message);
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Login failed!";

      if (error?.response?.status === 400) {
        toast.error("User not found! Please check your email/password.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };


  const handleOTPVerification = async () => {
    try {
      await verifyUser(formData.email, formData.otp)
        .then((res) => dispatch(setMessage(res.message)))
        .catch((err) => dispatch(setMessage(err?.response?.message)));
      console.log("OTP verified");
      navigate("/");
    } catch (error) {
      console.error("OTP verification failed:", error);
    }
  };

  return (
    <Container maxWidth={"sm"}>
      <Stack direction={"column"}>
        <h4 style={{ textAlign: "center" }}>{t("userLogin.heading")}</h4>
        <TextField
          variant="outlined"
          label={t("userLogin.email")}
          placeholder={t("userLogin.email")}
          name="email"
          value={formData.email}
          onChange={handleChange}
        />
        {!showOTPInput ? (
          <TextField
            sx={{ marginTop: 2 }}
            variant="outlined"
            label={t("userLogin.password")}
            placeholder={t("userLogin.password")}
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                showOTPInput ? handleOTPVerification() : handleLogin();
              }
            }}
          />
        ) : null}
        {showOTPInput && (
          <TextField
            sx={{ marginTop: 2 }}
            variant="outlined"
            label={t("userLogin.otp")}
            placeholder={t("userLogin.enterOtp")}
            name="otp"
            value={formData.otp}
            onChange={handleChange}
          />
        )}

        <Button
          variant="contained"
          sx={{ marginTop: 2 }}
          onClick={showOTPInput ? handleOTPVerification : handleLogin}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: "white" }} />
          ) : showOTPInput ? (
            t("userLogin.verifyOtp")
          ) : (
            t("userLogin.loginBtn")
          )}
        </Button>

        <h6 style={{ marginTop: 15, textAlign: "center" }}>
          {t("userLogin.noAccount")}{" "}
          <span>
            <a href="usersignup">{t("userLogin.signupNow")}</a>
          </span>{" "}
          {/* now. */}
        </h6>
        <h6 style={{ marginTop: 2, textAlign: "center" }}>
          <a href="forgotpassword">{t("userLogin.forgotPassword")}</a>
        </h6>
      </Stack>

    </Container>
  );
};

export default UserLogin;
